import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesAPI, groupsAPI, importAPI, googleAPI } from '../../services/api';
import SalespersonLayout from '../../layouts/SalespersonLayout';

const NewQuote = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [groupQuery, setGroupQuery] = useState('');
  const [groupResults, setGroupResults] = useState([]);
  const [searchingGroups, setSearchingGroups] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false });
  const [sheetUrl, setSheetUrl] = useState('');
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [sheetPreview, setSheetPreview] = useState(null);

  const [formData, setFormData] = useState({
    web_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    web_notes: '',
    discount_percent: 0
  });

  const [selectedWeb, setSelectedWeb] = useState(null);

  const [products, setProducts] = useState([
    { product_code: '', description: '', price_with_iva: '', quantity: 1 }
  ]);

  useEffect(() => {
    checkGoogleStatus();
  }, []);

  const checkGoogleStatus = async () => {
    try {
      const response = await googleAPI.getStatus();
      setGoogleStatus(response.data);
    } catch (err) {
      console.error('Error checking Google status:', err);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await quotesAPI.searchWeb(query);
      setSearchResults(response.data.webs);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearching(false);
    }
  };

  const selectWeb = (web) => {
    setSelectedWeb(web);
    setFormData({ ...formData, web_id: web.id });
    setSearchQuery(web.name);
    setSearchResults([]);
  };

  const handleGroupSearch = async (query) => {
    setGroupQuery(query);
    if (query.length < 2) {
      setGroupResults([]);
      return;
    }

    setSearchingGroups(true);
    try {
      const response = await groupsAPI.search(query, formData.web_id);
      setGroupResults(response.data.groups);
    } catch (err) {
      console.error('Error searching groups:', err);
    } finally {
      setSearchingGroups(false);
    }
  };

  const addGroupProducts = async (groupId) => {
    try {
      const response = await groupsAPI.getById(groupId);
      const group = response.data.group;

      if (!group.products || group.products.length === 0) {
        console.error('No products in group');
        return;
      }

      const newProducts = group.products.map(p => ({
        product_code: p.product_code || '',
        description: p.description,
        price_with_iva: p.price_with_iva,
        quantity: p.quantity
      }));

      if (products.length === 1 && !products[0].description && !products[0].price_with_iva) {
        setProducts(newProducts);
      } else {
        setProducts([...products, ...newProducts]);
      }

      setGroupQuery('');
      setGroupResults([]);
    } catch (err) {
      console.error('Error adding group:', err);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await importAPI.preview(file);
      setImportPreview(response.data);
      setShowImportModal(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al procesar archivo');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const confirmImport = () => {
    if (!importPreview?.products) return;

    const newProducts = importPreview.products.map(p => ({
      product_code: p.product_code || '',
      description: p.description,
      price_with_iva: p.price_with_iva,
      quantity: p.quantity
    }));

    if (products.length === 1 && !products[0].description && !products[0].price_with_iva) {
      setProducts(newProducts);
    } else {
      setProducts([...products, ...newProducts]);
    }

    setShowImportModal(false);
    setImportPreview(null);
  };

  const downloadTemplate = async () => {
    try {
      const response = await importAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_productos.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar plantilla');
    }
  };

  const connectGoogle = async () => {
    try {
      const response = await googleAPI.getAuthUrl();
      window.location.href = response.data.url;
    } catch (err) {
      alert(err.response?.data?.message || 'Error al conectar con Google');
    }
  };

  const loadGoogleSheet = async () => {
    if (!sheetUrl.trim()) return;

    setLoadingSheet(true);
    try {
      const response = await googleAPI.previewSheet(sheetUrl);
      setSheetPreview(response.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al cargar spreadsheet');
    } finally {
      setLoadingSheet(false);
    }
  };

  const confirmGoogleImport = () => {
    if (!sheetPreview?.products) return;

    const newProducts = sheetPreview.products.map(p => ({
      product_code: p.product_code || '',
      description: p.description,
      price_with_iva: p.price_with_iva,
      quantity: p.quantity
    }));

    if (products.length === 1 && !products[0].description && !products[0].price_with_iva) {
      setProducts(newProducts);
    } else {
      setProducts([...products, ...newProducts]);
    }

    setShowGoogleModal(false);
    setSheetPreview(null);
    setSheetUrl('');
  };

  const addProduct = () => {
    setProducts([...products, { product_code: '', description: '', price_with_iva: '', quantity: 1 }]);
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const updateProduct = (index, field, value) => {
    const updated = [...products];
    updated[index][field] = value;
    setProducts(updated);
  };

  const calculateTotals = () => {
    let subtotal = 0;
    products.forEach(p => {
      const price = parseFloat(p.price_with_iva) || 0;
      const qty = parseInt(p.quantity) || 0;
      const priceWithoutIva = price / 1.16;
      subtotal += priceWithoutIva * qty;
    });

    const discountPercent = parseFloat(formData.discount_percent) || 0;
    const discountAmount = subtotal * (discountPercent / 100);
    const subtotalAfterDiscount = subtotal - discountAmount;
    const iva = subtotalAfterDiscount * 0.16;
    const total = subtotalAfterDiscount + iva;

    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      iva: iva.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.web_id) {
        throw new Error('Selecciona una web');
      }
      if (!formData.client_name.trim()) {
        throw new Error('Ingresa el nombre del cliente');
      }
      if (!formData.web_notes.trim()) {
        throw new Error('El campo WEB es requerido');
      }
      if (products.some(p => !p.description.trim())) {
        throw new Error('Todos los productos deben tener descripcion');
      }
      if (products.some(p => !p.price_with_iva || parseFloat(p.price_with_iva) <= 0)) {
        throw new Error('Todos los productos deben tener precio valido');
      }

      const response = await quotesAPI.create({
        ...formData,
        products: products.map(p => ({
          ...p,
          price_with_iva: parseFloat(p.price_with_iva),
          quantity: parseInt(p.quantity)
        }))
      });

      navigate(`/quotes/${response.data.quote.id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al crear cotizacion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SalespersonLayout>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Nueva Cotizacion</h2>
      </div>

      <div className="max-w-5xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Web Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Seleccionar Web</h2>

            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar web por nombre o codigo..."
                className="input w-full"
              />
              {searching && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin h-5 w-5 border-2 border-primary-600 rounded-full border-t-transparent"></div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((web) => (
                    <button
                      key={web.id}
                      type="button"
                      onClick={() => selectWeb(web)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <p className="font-medium text-gray-900">{web.name}</p>
                      <p className="text-sm text-gray-500">
                        Codigo: {web.code} | Prefijo: {web.quote_prefix}
                      </p>
                      {web.bank_name && (
                        <p className="text-xs text-gray-400">Banco: {web.bank_name}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedWeb && (
              <div className="mt-4 p-4 bg-primary-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium text-primary-900">{selectedWeb.name}</p>
                  <p className="text-sm text-primary-700">
                    Prefijo: {selectedWeb.quote_prefix} | RFC: {selectedWeb.rfc_code || 'N/A'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedWeb(null);
                    setFormData({ ...formData, web_id: '' });
                    setSearchQuery('');
                  }}
                  className="text-primary-600 hover:text-primary-800 text-sm"
                >
                  Cambiar
                </button>
              </div>
            )}
          </div>

          {/* Client Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos del Cliente</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Nombre *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="input"
                  placeholder="Nombre del cliente"
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="input"
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <label className="label">Telefono</label>
                <input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="input"
                  placeholder="55 1234 5678"
                />
              </div>
            </div>

            {/* WEB Field */}
            <div className="mt-4">
              <label className="label">WEB *</label>
              <textarea
                value={formData.web_notes}
                onChange={(e) => setFormData({ ...formData, web_notes: e.target.value })}
                className="input w-full min-h-[100px] resize-y"
                placeholder="Ingrese la informacion de la web..."
                required
                style={{ minHeight: '100px' }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Este campo se mostrara en la cotizacion PDF
              </p>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Productos</h2>
              <div className="flex space-x-2">
                {googleStatus.configured && (
                  <button
                    type="button"
                    onClick={() => setShowGoogleModal(true)}
                    className="btn btn-secondary text-sm flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.5 9h-15A1.5 1.5 0 003 10.5v3A1.5 1.5 0 004.5 15h15a1.5 1.5 0 001.5-1.5v-3A1.5 1.5 0 0019.5 9z" fill="#34A853"/>
                      <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v3A1.5 1.5 0 004.5 9h15A1.5 1.5 0 0021 7.5v-3A1.5 1.5 0 0019.5 3z" fill="#4285F4"/>
                      <path d="M19.5 15h-15A1.5 1.5 0 003 16.5v3A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-3a1.5 1.5 0 00-1.5-1.5z" fill="#FBBC05"/>
                    </svg>
                    Sheets
                  </button>
                )}
                <label className="btn btn-secondary text-sm cursor-pointer">
                  {importing ? 'Procesando...' : 'Excel'}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={importing}
                  />
                </label>
                <button
                  type="button"
                  onClick={addProduct}
                  className="btn btn-secondary text-sm"
                >
                  + Agregar
                </button>
              </div>
            </div>

            {/* Group/Package Search */}
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <label className="label text-blue-800">Agregar Paquete/Grupo</label>
              <div className="relative">
                <input
                  type="text"
                  value={groupQuery}
                  onChange={(e) => handleGroupSearch(e.target.value)}
                  placeholder="Buscar paquete por nombre..."
                  className="input w-full"
                />
                {searchingGroups && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                  </div>
                )}
                {groupResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {groupResults.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => addGroupProducts(group.id)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{group.name}</p>
                            <p className="text-sm text-gray-500">{group.product_count} productos</p>
                          </div>
                          <span className="text-blue-600 font-semibold">
                            ${parseFloat(group.total_value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {products.map((product, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-gray-500">Producto {index + 1}</span>
                    {products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-2">
                      <label className="label text-xs">Codigo</label>
                      <input
                        type="text"
                        value={product.product_code}
                        onChange={(e) => updateProduct(index, 'product_code', e.target.value)}
                        className="input text-sm"
                        placeholder="SKU"
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="label text-xs">Descripcion *</label>
                      <input
                        type="text"
                        value={product.description}
                        onChange={(e) => updateProduct(index, 'description', e.target.value)}
                        className="input text-sm"
                        placeholder="Descripcion del producto"
                        required
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="label text-xs">Precio con IVA *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={product.price_with_iva}
                        onChange={(e) => updateProduct(index, 'price_with_iva', e.target.value)}
                        className="input text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label text-xs">Cantidad *</label>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) => updateProduct(index, 'quantity', e.target.value)}
                        className="input text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discount & Totals */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Discount */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Descuento</h2>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    className="input w-24"
                  />
                  <span className="text-gray-600">%</span>
                  {parseFloat(formData.discount_percent) > 0 && (
                    <span className="text-green-600 font-medium">
                      -${totals.discountAmount}
                    </span>
                  )}
                </div>
              </div>

              {/* Totals */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Totales</h2>
                <div className="space-y-2 text-right">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${totals.subtotal}</span>
                  </div>
                  {parseFloat(formData.discount_percent) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Descuento ({formData.discount_percent}%):</span>
                      <span>-${totals.discountAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">IVA (16%):</span>
                    <span className="font-medium">${totals.iva}</span>
                  </div>
                  <div className="flex justify-between text-xl pt-2 border-t">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold text-primary-600">${totals.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/quotes')}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Creando...' : 'Crear Cotizacion'}
            </button>
          </div>
        </form>
      </div>

      {/* Google Sheets Modal */}
      {showGoogleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Importar desde Google Sheets</h3>
              <button onClick={() => { setShowGoogleModal(false); setSheetPreview(null); }} className="text-gray-500">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {!googleStatus.connected ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">Conecta tu cuenta de Google para importar desde Sheets</p>
                  <button onClick={connectGoogle} className="btn btn-primary">
                    Conectar Google
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-green-600 mb-2">Conectado como: {googleStatus.googleEmail}</p>
                    <label className="label">URL del Spreadsheet</label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={sheetUrl}
                        onChange={(e) => setSheetUrl(e.target.value)}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        className="input flex-1"
                      />
                      <button
                        type="button"
                        onClick={loadGoogleSheet}
                        disabled={loadingSheet}
                        className="btn btn-primary"
                      >
                        {loadingSheet ? 'Cargando...' : 'Cargar'}
                      </button>
                    </div>
                  </div>

                  {sheetPreview && (
                    <div className="mt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-green-600 font-medium">
                          {sheetPreview.products.length} productos validos
                        </span>
                        {sheetPreview.invalidCount > 0 && (
                          <span className="text-red-600">{sheetPreview.invalidCount} invalidos</span>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Codigo</th>
                              <th className="px-3 py-2 text-left">Descripcion</th>
                              <th className="px-3 py-2 text-right">Precio</th>
                              <th className="px-3 py-2 text-right">Cant</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {sheetPreview.products.slice(0, 20).map((p, i) => (
                              <tr key={i}>
                                <td className="px-3 py-2 font-mono text-xs">{p.product_code || '-'}</td>
                                <td className="px-3 py-2">{p.description}</td>
                                <td className="px-3 py-2 text-right">${p.price_with_iva.toFixed(2)}</td>
                                <td className="px-3 py-2 text-right">{p.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {sheetPreview && sheetPreview.products.length > 0 && (
              <div className="p-6 border-t flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowGoogleModal(false); setSheetPreview(null); }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmGoogleImport}
                  className="btn btn-primary"
                >
                  Importar {sheetPreview.products.length} productos
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && importPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Vista Previa de Importacion</h3>
            </div>
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <div className="flex justify-between mb-4">
                <div>
                  <span className="text-green-600 font-medium">{importPreview.validCount} productos validos</span>
                  {importPreview.invalidCount > 0 && (
                    <span className="text-red-600 ml-4">{importPreview.invalidCount} invalidos</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Descargar plantilla
                </button>
              </div>

              {importPreview.products.length > 0 && (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Codigo</th>
                      <th className="px-3 py-2 text-left">Descripcion</th>
                      <th className="px-3 py-2 text-right">Precio</th>
                      <th className="px-3 py-2 text-right">Cant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {importPreview.products.map((p, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 font-mono text-xs">{p.product_code || '-'}</td>
                        <td className="px-3 py-2">{p.description}</td>
                        <td className="px-3 py-2 text-right">${p.price_with_iva.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{p.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {importPreview.products.length < importPreview.validCount && (
                <p className="mt-2 text-sm text-gray-500">
                  Mostrando {importPreview.products.length} de {importPreview.validCount} productos
                </p>
              )}
            </div>
            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => { setShowImportModal(false); setImportPreview(null); }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmImport}
                className="btn btn-primary"
              >
                Importar {importPreview.validCount} productos
              </button>
            </div>
          </div>
        </div>
      )}
    </SalespersonLayout>
  );
};

export default NewQuote;
